class AboutNewArrivals extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.track = this.querySelector('[data-track]');
    if (!this.track) return;
    this.slides = [...this.querySelectorAll('[data-slide]')];
    this.previous = this.querySelector('[data-prev]');
    this.next = this.querySelector('[data-next]');
    this.status = this.querySelector('[data-status]');
    this.motion = matchMedia('(prefers-reduced-motion: reduce)');
    this.paused = this.dataset.autoplay !== 'true';
    this.keyboardMode = false;
    this.dragging = false;
    this.hovered = false;
    this.nextAdvance = 0;
    this.interval = Math.max(3000, Number(this.dataset.interval) || 6000);
    this.inView = false;
    this.positions = [];
    this.page = 0;
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.previous.addEventListener('click', () => this.navigate(-1), options);
    this.next.addEventListener('click', () => this.navigate(1), options);
    document.addEventListener('keydown', (event) => {
      if (!['Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      this.keyboardMode = true;
      this.schedule();
    }, { ...options, capture: true });
    document.addEventListener('pointerdown', () => {
      this.keyboardMode = false;
    }, { ...options, capture: true });
    this.addEventListener('pointerenter', (event) => {
      if (event.pointerType === 'touch') return;
      this.hovered = true;
      this.nextAdvance = 0;
      this.schedule();
    }, options);
    this.addEventListener('pointerleave', (event) => {
      if (event.pointerType === 'touch') return;
      this.hovered = false;
      this.nextAdvance = Date.now() + this.interval;
      this.schedule();
    }, options);
    this.addEventListener('focusin', () => this.schedule(), options);
    this.addEventListener('focusout', () => {
      queueMicrotask(() => this.schedule());
    }, options);
    this.track.addEventListener('pointerdown', () => {
      this.dragging = true;
      this.stop();
    }, options);
    document.addEventListener('pointerup', () => {
      this.dragging = false;
      this.schedule();
    }, options);
    document.addEventListener('pointercancel', () => {
      this.dragging = false;
      this.schedule();
    }, options);
    this.track.addEventListener('wheel', () => {
      this.stop();
      this.nextAdvance = Date.now() + this.interval;
      clearTimeout(this.settleTimer);
      this.settleTimer = setTimeout(() => this.settle(), 150);
    }, { ...options, passive: true });
    this.track.addEventListener('keydown', (event) => {
      if (event.target !== this.track) return;
      const direction = this.rtl ? -1 : 1;
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        event.preventDefault();
        this.navigate((event.key === 'ArrowRight' ? 1 : -1) * direction);
      } else if (event.key === 'Home' || event.key === 'End') {
        event.preventDefault();
        this.pause();
        this.goTo(event.key === 'Home' ? 0 : this.positions.length - 1);
      }
    }, options);
    this.track.addEventListener('scroll', () => {
      this.stop();
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.update());
      clearTimeout(this.settleTimer);
      this.settleTimer = setTimeout(() => this.settle(), 150);
    }, { ...options, passive: true });
    document.addEventListener('visibilitychange', () => this.schedule(), options);
    this.motion.addEventListener('change', () => {
      this.schedule();
    }, options);
    this.addEventListener('click', (event) => {
      const button = event.target.closest('[data-arrivals-open]');
      if (!button || event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const panel = this.querySelector('about-cart-related[data-context="catalogue"]');
      if (typeof panel?.openCatalogueProduct !== 'function') return;
      event.preventDefault();
      this.stop();
      panel.openCatalogueProduct(button, {
        id: button.dataset.productId,
        title: button.dataset.productTitle,
        url: button.href,
        price: Number(button.dataset.productPrice),
        price_varies: button.dataset.priceVaries === 'true'
      });
    }, options);
    this.observer = new ResizeObserver(() => this.measure());
    this.observer.observe(this.track);
    this.visibility = new IntersectionObserver(([entry]) => {
      this.inView = entry.isIntersecting;
      this.schedule();
    }, { threshold: 0.15 });
    this.visibility.observe(this);
    this.measure();
    this.dataset.ready = '';
  }

  disconnectedCallback() {
    this.stop();
    clearTimeout(this.settleTimer);
    cancelAnimationFrame(this.frame);
    this.observer?.disconnect();
    this.visibility?.disconnect();
    this.controller?.abort();
    this.controller = null;
  }

  scrollPosition() {
    return Math.abs(this.track.scrollLeft);
  }

  measure() {
    if (!this.track.clientWidth || !this.slides.length) return;
    const oldIndex = this.offsets?.findIndex((offset) => offset >= this.scrollPosition() - 2) ?? 0;
    this.rtl = getComputedStyle(this.track).direction === 'rtl';
    const bounds = this.track.getBoundingClientRect();
    const scroll = this.scrollPosition();
    this.offsets = this.slides.map((slide) => {
      const box = slide.getBoundingClientRect();
      return (this.rtl ? bounds.right - box.right : box.left - bounds.left) + scroll;
    });
    this.widths = this.slides.map((slide) => slide.getBoundingClientRect().width);
    const maximum = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
    const positions = [];
    for (let index = 0; index < this.slides.length; index += 1) {
      const position = Math.min(maximum, Math.max(0, this.offsets[index]));
      if (!positions.length || position - positions[positions.length - 1] > 2) positions.push(position);
    }
    if (maximum - (positions.at(-1) ?? 0) > 2) positions.push(maximum);
    this.positions = positions;
    this.previous.hidden = positions.length < 2;
    this.next.hidden = positions.length < 2;
    this.style.setProperty('--arrivals-media-center', (this.widths[0] / 2 + 3) + 'px');
    this.targetPage = null;
    if (oldIndex > 0 && this.offsets[oldIndex] !== undefined) {
      this.track.scrollTo({ left: Math.min(maximum, this.offsets[oldIndex]) * (this.rtl ? -1 : 1), behavior: 'instant' });
    }
    this.update();
    this.schedule();
  }

  update() {
    const scroll = this.scrollPosition();
    this.page = this.positions.reduce((best, position, index) => Math.abs(position - scroll) < Math.abs(this.positions[best] - scroll) ? index : best, 0);
    const currentPage = this.targetPage ?? this.page;
    this.previous.disabled = currentPage === 0;
    this.next.disabled = currentPage === this.positions.length - 1;
    const visible = this.offsets.map((offset, index) => ({ offset, index }))
      .filter(({ offset, index }) => offset + this.widths[index] > scroll + 2 && offset < scroll + this.track.clientWidth - 2);
    const first = (visible[0]?.index ?? 0) + 1;
    const last = (visible.at(-1)?.index ?? 0) + 1;
    this.rangeText = this.dataset.rangeLabel.replace('{first}', String(first)).replace('{last}', String(last)).replace('{total}', String(this.slides.length));
  }

  navigate(direction) {
    this.pause();
    this.goTo(Math.max(0, Math.min(this.positions.length - 1, (this.targetPage ?? this.page) + direction)));
  }

  goTo(page, automatic = false) {
    if (!Number.isFinite(this.positions[page])) return;
    this.stop();
    if (!automatic) this.nextAdvance = Date.now() + this.interval;
    this.targetPage = page;
    this.track.scrollTo({ left: this.positions[page] * (this.rtl ? -1 : 1), behavior: this.motion.matches ? 'instant' : 'smooth' });
    this.update();
    clearTimeout(this.settleTimer);
    this.settleTimer = setTimeout(() => this.settle(), 600);
  }

  settle() {
    if (!this.isConnected) return;
    this.targetPage = null;
    this.update();
    if (this.keyboardMode && this.contains(document.activeElement)) this.status.textContent = this.rangeText;
    this.schedule();
  }

  pause() {
    this.stop();
  }

  stop() {
    clearTimeout(this.timer);
    this.timer = null;
  }

  schedule() {
    this.stop();
    const focused = this.keyboardMode && this.contains(document.activeElement);
    const panelOpen = this.querySelector('dialog[open]') !== null;
    if (!this.isConnected || this.paused || this.dragging || this.hovered || focused || panelOpen || !this.inView || document.hidden || this.positions.length < 2) {
      this.nextAdvance = 0;
      return;
    }
    if (this.targetPage != null) return;
    if (!this.nextAdvance) this.nextAdvance = Date.now() + this.interval;
    this.timer = setTimeout(() => {
      this.nextAdvance = Date.now() + this.interval;
      this.goTo((this.page + 1) % this.positions.length, true);
    }, Math.max(0, this.nextAdvance - Date.now()));
  }
}

if (!customElements.get('about-new-arrivals')) customElements.define('about-new-arrivals', AboutNewArrivals);
